import LanguageSelector from '../i18n/LanguageSelector';
import { useAppTranslation } from '../i18n/useAppTranslation';
import React, { useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Logout,
  Message,
  ExpandLess,
  ExpandMore,
  Brightness3,
  WbSunny,
  Contrast,
  Email,
  MenuBook,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useColorMode } from "../context/ColorModeContext";
import { getSidebarBackground } from "../theme";
import { getMediaUrl } from "../utils/imageUrl";
import { isNativeApp } from "../utils/platform";
import api from "../services/api";

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 72;

export interface SidebarSubItem {
  label: string;
  icon: React.ReactNode;
  subIndex: number;
}

export interface SidebarItem {
  label: string;
  icon: React.ReactNode;
  index: number;
  children?: SidebarSubItem[];
  navigateTo?: string;
  navigateState?: Record<string, any>;
  badge?: number;
}

interface SidebarProps {
  items: SidebarItem[];
  activeIndex: number;
  onSelect: (index: number) => void;
  activeSubIndex?: number;
  onSubSelect?: (subIndex: number) => void;
  user: any;
  onLogout: () => void;
  role: string;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export default function Sidebar({
  items,
  activeIndex,
  onSelect,
  activeSubIndex,
  onSubSelect,
  user,
  onLogout,
  role,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  const { tr } = useAppTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isLandscape = useMediaQuery(
    "(orientation: landscape) and (max-height: 500px)"
  );
  const [collapsed, setCollapsed] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsId = React.useId();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [docOpen, setDocOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSubject, setContactSubject] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactSending, setContactSending] = useState(false);
  const [contactStatus, setContactStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const { mode: themeMode, setThemeMode, toggleColorMode } = useColorMode();
  const navigate = useNavigate();

  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  const isDark = theme.palette.mode === "dark";

  const roleLabel =
    role === "COACH" ? "Coach" : role === "SPORTIF" ? "Sportif" : "Admin";
  const dashboardPath =
    role === "COACH" ? "/coach/dashboard" : "/sportif/dashboard";

  const docSectionsAll = role === 'COACH' ? [
    { title: 'Tableau de bord', content: 'Consultez vos séances à venir, vos sportifs et vos revenus depuis le tableau de bord.' },
    { title: 'Planning', content: 'Créez une séance ou un créneau disponible. Modifiez ou supprimez les créneaux avec les boutons correspondants. Vos sportifs peuvent réserver les disponibilités publiées.' },
    { title: 'Mes sportifs', content: 'Consultez les profils, ouvrez une conversation, confirmez un paiement ou terminez une séance pour évaluer le sportif. Invitez vos sportifs par e-mail.' },
    { title: 'Programme', content: 'Créez des programmes adaptés aux objectifs de vos sportifs et associez-les aux séances.' },
    { title: 'Messages', content: 'Échangez avec vos contacts et vos groupes. Le badge indique les messages non lus.' },
    { title: 'Paiements', content: 'Consultez les paiements déclarés et votre abonnement Coach Pro. Les séances se règlent directement entre le sportif et le coach.' },
    { title: 'Mon profil', content: 'Complétez votre photo, votre sport, votre présentation, vos qualifications, vos tarifs et votre localisation. Choisissez votre langue dans le menu Thème et aide.' },
  ] : [
    { title: 'Tableau de bord', content: 'Retrouvez vos prochaines séances, vos réservations et votre progression depuis le tableau de bord.' },
    { title: 'Trouver un coach', content: 'Filtrez les coachs par ville et par sport, consultez leur profil et choisissez un programme ou une séance libre, puis un créneau disponible.' },
    { title: 'Mes Performances', content: 'Consultez les évaluations de votre coach, les graphiques et votre historique. Les résultats apparaissent après vos séances évaluées.' },
    { title: 'Messages', content: 'Échangez avec vos contacts et vos groupes. Le badge indique les messages non lus.' },
    { title: 'Mon profil', content: 'Mettez à jour votre photo, votre sport, vos objectifs et les informations utiles à votre suivi. Choisissez votre langue dans le menu Thème et aide.' },
  ];

  // Sur l'app native (iOS/Android), on retire toute rubrique d'aide qui evoque
  // le paiement/abonnement (regles des stores — pas de reference a un achat externe).
  const docSections = isNativeApp()
    ? docSectionsAll.filter((s) => !/abonnement|paiement/i.test(s.title))
    : docSectionsAll;

  const sidebarContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: getSidebarBackground(themeMode),
        color: "white",
        overflow: "hidden",
        // Descend le contenu sous la barre d'etat / encoche sur mobile natif
        // (l'entete de la sidebar cachait l'heure en portrait). Nul sur desktop.
        pt: "env(safe-area-inset-top)",
      }}
    >
      {/* Logo & collapse button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 1 : 2,
          py: 2,
          minHeight: 72,
        }}
      >
        {!collapsed && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
            }}
            onClick={() => navigate(dashboardPath)}
          >
            <Box
              component="img"
              src="/g1s-logo-3d.png"
              alt="G1Sport"
              sx={{ height: 36 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                color: "#FFD700",
                letterSpacing: "-0.02em",
              }}
            >
              G1Sport
            </Typography>
          </Box>
        )}
        {(!isMobile || isLandscape) && (
          <IconButton
            size="small"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={
              collapsed ? tr("Déplier le menu latéral") : tr("Replier le menu latéral")
            }
            aria-expanded={!collapsed}
            sx={{
              color: "rgba(255,255,255,0.6)",
              "&:hover": { color: "white" },
            }}
          >
            {collapsed ? (
              <ChevronRight fontSize="small" />
            ) : (
              <ChevronLeft fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

      {/* User info */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: collapsed ? 1 : 2,
          py: 2,
          justifyContent: collapsed ? "center" : "flex-start",
        }}
      >
        <Avatar
          src={
            getMediaUrl(
              user?.coach_profile?.photo || user?.sportif_profile?.photo
            ) || undefined
          }
          sx={{
            width: 40,
            height: 40,
            bgcolor: "primary.main",
            fontWeight: 700,
            fontSize: "1rem",
            flexShrink: 0,
          }}
        >
          {user?.username?.charAt(0).toUpperCase()}
        </Avatar>
        {!collapsed && (
          <Box sx={{ overflow: "hidden" }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: "white",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user?.username}
            </Typography>
            <Chip
              label={roleLabel}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.65rem",
                fontWeight: 700,
                bgcolor: role === "COACH" ? "primary.main" : "secondary.main",
                color: "white",
                mt: 0.25,
              }}
            />
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 1 }} />

      {/* Navigation items */}
      <List sx={{ flexGrow: 1, px: 1, overflowY: "auto", overflowX: "hidden" }}>
        {items.map((item) => {
          const isActive = activeIndex === item.index;
          const hasChildren =
            !collapsed && item.children && item.children.length > 0;
          const isExpanded = expandedIndex === item.index && hasChildren;
          return (
            <React.Fragment key={item.index}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <Tooltip
                  title={collapsed ? tr(item.label) : ""}
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    selected={isActive}
                    aria-label={tr(item.label)}
                    aria-current={isActive ? "page" : undefined}
                    aria-expanded={hasChildren ? isExpanded : undefined}
                    onClick={() => {
                      if (hasChildren) {
                        if (isExpanded) {
                          // Déjà déplié → naviguer vers l'onglet parent
                          onSelect(item.index);
                          if (isMobile) onMobileClose();
                        } else {
                          // Replié → déplier + naviguer
                          setExpandedIndex(item.index);
                          onSelect(item.index);
                          if (isMobile) onMobileClose();
                        }
                      } else if (item.navigateTo) {
                        navigate(item.navigateTo, {
                          state: item.navigateState,
                        });
                        if (isMobile) onMobileClose();
                      } else {
                        onSelect(item.index);
                        if (isMobile) onMobileClose();
                      }
                    }}
                    sx={{
                      borderRadius: 2,
                      px: collapsed ? 1.5 : 2,
                      py: 1.25,
                      justifyContent: collapsed ? "center" : "flex-start",
                      minHeight: 48,
                      bgcolor: isActive
                        ? "rgba(255,255,255,0.08)"
                        : "transparent",
                      backdropFilter: isActive ? "blur(8px)" : "none",
                      border: isActive
                        ? "1px solid rgba(255,255,255,0.18)"
                        : "1px solid transparent",
                      boxShadow: isActive ? "inset 3px 0 0 #D9B76A" : "none",
                      "&:hover": {
                        bgcolor: isActive
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(255,255,255,0.06)",
                        boxShadow: isActive ? "inset 3px 0 0 #D9B76A" : "none",
                      },
                      "&.Mui-selected": {
                        bgcolor: "rgba(255,255,255,0.08)",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive ? "white" : "rgba(255,255,255,0.6)",
                        minWidth: collapsed ? 0 : 36,
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      {item.icon}
                      {item.badge && item.badge > 0 ? (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -4,
                            right: collapsed ? -4 : 2,
                            bgcolor: "#ef4444",
                            color: "#fff",
                            borderRadius: "50%",
                            minWidth: 16,
                            height: 16,
                            fontSize: "0.6rem",
                            fontWeight: 800,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 0.3,
                            lineHeight: 1,
                            border: "1.5px solid #111",
                          }}
                        >
                          {item.badge > 99 ? "99+" : item.badge}
                        </Box>
                      ) : null}
                    </ListItemIcon>
                    {!collapsed && (
                      <>
                        <ListItemText
                          primary={tr(item.label)}
                          primaryTypographyProps={{
                            fontSize: "0.875rem",
                            fontWeight: isActive ? 700 : 500,
                            color: isActive
                              ? "white"
                              : "rgba(255,255,255,0.75)",
                          }}
                        />
                        {hasChildren &&
                          (isExpanded ? (
                            <ExpandLess
                              sx={{
                                color: "rgba(255,255,255,0.8)",
                                fontSize: 18,
                              }}
                            />
                          ) : (
                            <ExpandMore
                              sx={{
                                color: "rgba(255,255,255,0.6)",
                                fontSize: 18,
                              }}
                            />
                          ))}
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>

              {/* Sub-items */}
              {hasChildren && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List disablePadding sx={{ pl: 1.5, mb: 0.5 }}>
                    {item.children!.map((child) => {
                      const isSubActive =
                        isActive && activeSubIndex === child.subIndex;
                      return (
                        <ListItem
                          key={child.subIndex}
                          disablePadding
                          sx={{ mb: 0.25 }}
                        >
                          <ListItemButton
                            selected={isSubActive}
                            onClick={() => {
                              onSelect(item.index);
                              if (onSubSelect) onSubSelect(child.subIndex);
                              if (isMobile) onMobileClose();
                            }}
                            sx={{
                              borderRadius: 2,
                              px: 1.5,
                              py: 0.875,
                              minHeight: 40,
                              bgcolor: isSubActive
                                ? "rgba(255,255,255,0.15)"
                                : "transparent",
                              borderLeft: isSubActive
                                ? "3px solid"
                                : "3px solid transparent",
                              borderColor: isSubActive
                                ? "#FFD700"
                                : "transparent",
                              "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                              "&.Mui-selected": {
                                bgcolor: "rgba(255,255,255,0.15)",
                                "&:hover": {
                                  bgcolor: "rgba(255,255,255,0.12)",
                                },
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: isSubActive
                                  ? "#FFD700"
                                  : "rgba(255,255,255,0.5)",
                                minWidth: 28,
                                justifyContent: "center",
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={tr(child.label)}
                              primaryTypographyProps={{
                                fontSize: "0.8rem",
                                fontWeight: isSubActive ? 700 : 400,
                                color: isSubActive
                                  ? "#FFD700"
                                  : "rgba(255,255,255,0.65)",
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mt: 1 }} />

      {/* Bottom actions */}
      <Box sx={{ px: 1, pt: 0.5, pb: isNativeApp() ? 0.5 : 6.5, flexShrink: 0 }}>
        <Tooltip title={collapsed ? tr("Thème et aide") : ""} placement="right" arrow>
          <ListItemButton
            component="button"
            aria-label={tr("Thème et aide")}
            aria-expanded={toolsOpen}
            aria-controls={toolsId}
            onClick={() => setToolsOpen((open) => !open)}
            sx={{ width: "100%", minHeight: 34, borderRadius: 2, px: 1.5, py: 0.5, color: "rgba(255,255,255,0.7)", justifyContent: "center" }}
          >
            {!collapsed && <ListItemText primary={tr("Thème et aide")} primaryTypographyProps={{ fontSize: "0.75rem", fontWeight: 600 }} />}
            {toolsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </ListItemButton>
        </Tooltip>
        <Collapse in={toolsOpen}>
          <Box sx={{ px: 1, py: 2 }}><LanguageSelector/></Box>
          <List id={toolsId} aria-label={tr("Thème et aide")} sx={{ p: 0 }}>
            {/* Sélecteur de thème */}
            <ListItem disablePadding sx={{ mb: 0.25 }}>
              {collapsed ? (
                <Tooltip title={tr("Changer le thème")} placement="right" arrow>
                  <ListItemButton
                    onClick={toggleColorMode}
                    sx={{
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.5,
                      minHeight: 34,
                      justifyContent: "center",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: "rgba(255,255,255,0.6)",
                        minWidth: 0,
                        justifyContent: "center",
                      }}
                    >
                      {themeMode === "light" ? (
                        <WbSunny fontSize="small" />
                      ) : themeMode === "dark" ? (
                        <Contrast fontSize="small" />
                      ) : (
                        <Brightness3 fontSize="small" />
                      )}
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              ) : (
                <Box sx={{ px: 1, py: 0.5, width: "100%" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.4)",
                      display: "block",
                      mb: 0.5,
                      fontSize: "0.6rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                     {tr("Thème")} </Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {(
                      [
                        {
                          key: "midnight",
                          label: tr("Nuit"),
                          icon: <Brightness3 sx={{ fontSize: 14 }} />,
                        },
                        {
                          key: "dark",
                          label: tr("Sombre"),
                          icon: <Contrast sx={{ fontSize: 14 }} />,
                        },
                        {
                          key: "light",
                          label: tr("Clair"),
                          icon: <WbSunny sx={{ fontSize: 14 }} />,
                        },
                      ] as const
                    ).map(({ key, label, icon }) => (
                      <Box component="button" type="button" aria-label={"Thème " + label} aria-pressed={themeMode === key} key={key} onClick={() => setThemeMode(key)}
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "center",
                          minHeight: 32,
                          alignItems: "center",
                          gap: 0.5,
                          py: 0.5,
                          px: 0.5,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          bgcolor:
                            themeMode === key
                              ? "rgba(251,146,60,0.25)"
                              : "rgba(255,255,255,0.05)",
                          border: "1px solid",
                          borderColor:
                            themeMode === key
                              ? "rgba(251,146,60,0.6)"
                              : "transparent",
                          transition: "all 0.15s ease",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                        }}
                      >
                        <Box
                          sx={{
                            color:
                              themeMode === key
                                ? "#FB923C"
                                : "rgba(255,255,255,0.5)",
                          }}
                        >
                          {icon}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color:
                              themeMode === key
                                ? "#FB923C"
                                : "rgba(255,255,255,0.45)",
                            fontWeight: themeMode === key ? 700 : 400,
                          }}
                        >
                          {tr(label)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </ListItem>

            {/* Nous contacter */}
            <ListItem disablePadding sx={{ mb: 0.25 }}>
              <Tooltip
                title={collapsed ? tr("Nous contacter") : ""}
                placement="right"
                arrow
              >
                <ListItemButton
                  onClick={() => {
                    setContactOpen(true);
                    setContactStatus("idle");
                    setContactSubject("");
                    setContactMessage("");
                  }}
                  sx={{
                    borderRadius: 2,
                    px: collapsed ? 1.5 : 2,
                    py: 0.5,
                    minHeight: 34,
                    justifyContent: collapsed ? "center" : "flex-start",
                    "&:hover": { bgcolor: "rgba(0,188,212,0.15)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "#00BCD4",
                      minWidth: collapsed ? 0 : 28,
                      justifyContent: "center",
                    }}
                  >
                    <Email fontSize="small" />
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={tr("Nous contacter")}
                      primaryTypographyProps={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "#00BCD4",
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>

            {/* Documentation */}
            <ListItem disablePadding sx={{ mb: 0.25 }}>
              <Tooltip
                title={collapsed ? tr("Documentation") : ""}
                placement="right"
                arrow
              >
                <ListItemButton
                  onClick={() => setDocOpen(true)}
                  sx={{
                    borderRadius: 2,
                    px: collapsed ? 1.5 : 2,
                    py: 0.5,
                    minHeight: 34,
                    justifyContent: collapsed ? "center" : "flex-start",
                    "&:hover": { bgcolor: "rgba(99,102,241,0.15)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "#6366F1",
                      minWidth: collapsed ? 0 : 28,
                      justifyContent: "center",
                    }}
                  >
                    <MenuBook fontSize="small" />
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={tr("Documentation")}
                      primaryTypographyProps={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "#6366F1",
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>

            <ListItem disablePadding>
              <Tooltip
                title={collapsed ? tr("Déconnexion") : ""}
                placement="right"
                arrow
              >
                <ListItemButton
                  onClick={onLogout}
                  sx={{
                    borderRadius: 2,
                    px: collapsed ? 1.5 : 2,
                    py: 0.5,
                    minHeight: 34,
                    justifyContent: collapsed ? "center" : "flex-start",
                    "&:hover": { bgcolor: "rgba(239,68,68,0.15)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "#f87171",
                      minWidth: collapsed ? 0 : 28,
                      justifyContent: "center",
                    }}
                  >
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={tr("Déconnexion")}
                      primaryTypographyProps={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "#f87171",
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          </List>
        </Collapse>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Dialog Nous contacter */}
      <Dialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, bgcolor: "#111827", color: "white" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            pb: 2,
          }}
        >
          <Email sx={{ color: "#00BCD4" }} />
          <Box>
            <Typography variant="h6" fontWeight={800}>
               {tr("Nous contacter")} </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.5)" }}
            >
               {tr("Réponse sous 24h à contact@g1sport.fr")} </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {contactStatus === "success" ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h4" sx={{ mb: 1 }}>
                ✅
              </Typography>
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                 {tr("Message envoyé !")} </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.6)" }}
              >
                 {tr("Nous vous répondrons sous 24h.")} </Typography>
            </Box>
          ) : (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    mb: 0.5,
                    display: "block",
                  }}
                >
                   {tr("Sujet *")} </Typography>
                <input
                  value={contactSubject}
                  onChange={(e) => setContactSubject(e.target.value)}
                  placeholder={tr("Ex : Problème de réservation, Question sur l'abonnement...")}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.15)",
                    background: "rgba(255,255,255,0.06)",
                    color: "white",
                    fontSize: "0.875rem",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    mb: 0.5,
                    display: "block",
                  }}
                >
                   {tr("Message *")} </Typography>
                <textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder={tr("Décrivez votre demande en détail...")}
                  rows={5}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.15)",
                    background: "rgba(255,255,255,0.06)",
                    color: "white",
                    fontSize: "0.875rem",
                    outline: "none",
                    resize: "vertical",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                  }}
                />
              </Box>
              {contactStatus === "error" && (
                <Typography variant="body2" sx={{ color: "#f87171" }}>
                   {tr("❌ Erreur lors de l'envoi. Réessayez ou écrivez à contact@g1sport.fr")} </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: "1px solid rgba(255,255,255,0.08)",
            px: 3,
            py: 2,
            gap: 1,
          }}
        >
          {contactStatus !== "success" ? (
            <>
              <Button
                onClick={() => setContactOpen(false)}
                sx={{ color: "rgba(255,255,255,0.5)", borderRadius: 2 }}
              >
                 {tr("Annuler")} </Button>
              <Button
                variant="contained"
                disabled={
                  contactSending ||
                  !contactSubject.trim() ||
                  !contactMessage.trim()
                }
                onClick={async () => {
                  setContactSending(true);
                  try {
                    await api.post("/users/contact/", {
                      subject: contactSubject,
                      message: contactMessage,
                    });
                    setContactStatus("success");
                  } catch {
                    setContactStatus("error");
                  }
                  setContactSending(false);
                }}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  bgcolor: "#00BCD4",
                  "&:hover": { bgcolor: "#0097A7" },
                }}
              >
                {contactSending ? "Envoi..." : tr("Envoyer le message")}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setContactOpen(false)}
              variant="contained"
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
               {tr("Fermer")} </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog Documentation */}
      <Dialog
        open={docOpen}
        onClose={() => setDocOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, bgcolor: "#111827", color: "white" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            pb: 2,
          }}
        >
          <MenuBook sx={{ color: "#6366F1" }} />
          <Box>
            <Typography variant="h6" fontWeight={800}>
               {tr("Documentation G1Sport")} </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.5)" }}
            >
               {tr("Guide d'utilisation —")}{" "}
              {role === "COACH" ? "Espace Coach" : "Espace Sportif"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.5)", mb: 2 }}
          >
             {tr("Cliquez sur chaque section pour afficher les instructions détaillées.")} </Typography>
          {docSections.map((section, i) => (
            <Accordion
              key={i}
              sx={{
                bgcolor: "rgba(255,255,255,0.04)",
                color: "white",
                mb: 1,
                borderRadius: "8px !important",
                border: "1px solid rgba(255,255,255,0.08)",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon sx={{ color: "rgba(255,255,255,0.6)" }} />
                }
              >
                <Typography fontWeight={700} fontSize="0.95rem">
                  {tr(section.title)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {tr(section.content).split("\n").map((line, j) => (
                  <Typography
                    key={j}
                    variant="body2"
                    sx={{
                      color: line.startsWith("•")
                        ? "rgba(255,255,255,0.9)"
                        : "rgba(255,255,255,0.65)",
                      fontWeight: line.startsWith("•") ? 500 : 400,
                      mb: 0.5,
                      lineHeight: 1.7,
                      pl: line.startsWith("•") ? 1 : 0,
                    }}
                  >
                    {line || "\u00a0"}
                  </Typography>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: "rgba(0,188,212,0.08)",
              border: "1px solid rgba(0,188,212,0.2)",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#00BCD4", fontWeight: 600 }}
            >
               {tr("💡 Besoin d'aide supplémentaire ?")} </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.6)", mt: 0.5 }}
            >
               {tr("Contactez-nous à")}{" "}
              <strong style={{ color: "#00BCD4" }}>contact@g1sport.fr</strong>  {tr("— nous répondons sous 24h.")} </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ borderTop: "1px solid rgba(255,255,255,0.08)", px: 3, py: 2 }}
        >
          <Button
            onClick={() => {
              setDocOpen(false);
              setContactOpen(true);
              setContactStatus("idle");
              setContactSubject("");
              setContactMessage("");
            }}
            startIcon={<Email />}
            variant="outlined"
            sx={{
              borderColor: "#00BCD4",
              color: "#00BCD4",
              borderRadius: 2,
              "&:hover": { bgcolor: "rgba(0,188,212,0.1)" },
            }}
          >
             {tr("Nous contacter")} </Button>
          <Button
            onClick={() => setDocOpen(false)}
            variant="contained"
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
             {tr("Fermer")} </Button>
        </DialogActions>
      </Dialog>

      {/* Desktop permanent sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: width,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: width,
            boxSizing: "border-box",
            border: "none",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: "hidden",
          },
        }}
        open
      >
        {sidebarContent}
      </Drawer>

      {/* Mobile temporary drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: SIDEBAR_WIDTH, border: "none" },
        }}
      >
        {sidebarContent}
      </Drawer>
    </>
  );
}
